import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Bell, ArrowLeft, X, Trash2 } from "lucide-react";
import { useDrafts } from "../../contexts/DraftsContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Bell icon with a count badge showing BOTH:
 *   1. In-progress drafts (forms the user started but didn't finish)
 *   2. Recent admin activity entries (delete/reject/discount events from the
 *      global activity_log)
 *
 * Dropdown has two sections so the user can resume work AND audit changes.
 */
const BellDraftsButton = ({ onJump }) => {
  const { drafts, clearDirty } = useDrafts();
  const [open, setOpen] = useState(false);
  const [logs, setLogs] = useState([]);
  const [unread, setUnread] = useState(0);
  const wrapRef = useRef(null);
  const entries = Object.entries(drafts || {});
  const draftCount = entries.length;
  const total = draftCount + unread;

  const fetchLog = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/activity-log`, { params: { limit: 20 } });
      setLogs(data.items || []);
      setUnread(data.unread_count || 0);
    } catch {
      // silent
    }
  };

  useEffect(() => {
    fetchLog();
    const t = setInterval(fetchLog, 20000); // poll every 20s
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // When dropdown opens, refresh + mark all as read after a short delay
  const onToggle = async () => {
    if (!open) await fetchLog();
    setOpen((v) => !v);
  };
  const markAllRead = async () => {
    try {
      await axios.post(`${API}/admin/activity-log/mark-read`, { all: true });
      setUnread(0);
    } catch {}
  };

  const fmtAgo = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    const diff = (Date.now() - d.getTime()) / 1000;
    if (diff < 60) return "الآن";
    if (diff < 3600) return `${Math.floor(diff / 60)} د`;
    if (diff < 86400) return `${Math.floor(diff / 3600)} س`;
    return `${Math.floor(diff / 86400)} يوم`;
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="hr-bell-icon"
        onClick={onToggle}
        title={total > 0 ? `${total} تنبيه` : "لا توجد تنبيهات"}
        aria-label="الإشعارات والمسوّدات"
        className="relative p-2 hover:bg-white/10 rounded-full transition-colors"
      >
        <Bell className={`w-5 h-5 ${total > 0 ? "text-[#F5A623] animate-pulse" : ""}`} />
        {total > 0 && (
          <span
            data-testid="hr-bell-badge"
            className="absolute -top-1 -right-1 bg-[#E85D45] text-white text-[10px] font-black rounded-full min-w-[20px] h-5 flex items-center justify-center px-1"
            dir="ltr"
          >
            {total > 99 ? "99+" : total}
          </span>
        )}
      </button>

      {open && (
        <div
          data-testid="hr-bell-dropdown"
          dir="rtl"
          className="absolute left-0 sm:left-auto sm:right-0 top-full mt-2 w-[360px] max-w-[94vw] bg-white text-[#1E3A5F] rounded-2xl shadow-2xl border-2 border-[#E4EBE6] overflow-hidden z-50"
        >
          {/* Drafts section */}
          {draftCount > 0 && (
            <>
              <div className="px-3 py-2 bg-[#FFFBEB] border-b-2 border-[#F59E0B] flex items-center justify-between">
                <span className="text-xs font-bold text-[#9A3412]">شُغل معلّق</span>
                <span className="text-[10px] bg-white/70 rounded-full px-2 py-0.5 font-bold" dir="ltr">{draftCount}</span>
              </div>
              <div className="max-h-40 overflow-y-auto divide-y divide-[#F3F6F4]">
                {entries.map(([tabId, d]) => (
                  <div key={tabId} data-testid={`hr-bell-row-${tabId}`} className="flex items-center gap-2 px-3 py-2 hover:bg-[#FFFBEB]">
                    <button type="button" onClick={() => { setOpen(false); onJump && onJump(tabId); }}
                      data-testid={`hr-bell-jump-${tabId}`}
                      className="flex-1 text-right text-xs font-bold text-[#9A3412] flex items-center gap-1 min-w-0">
                      <ArrowLeft className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{d.label}</span>
                    </button>
                    <button type="button" onClick={() => clearDirty(tabId)}
                      data-testid={`hr-bell-clear-${tabId}`}
                      title="إلغاء المسودة"
                      className="bg-[#FEE2E2] hover:bg-[#FECACA] text-[#E85D45] rounded-full p-1 shrink-0">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Activity Log section */}
          <div className="px-3 py-2 bg-[#EFF6FF] border-b-2 border-[#3B82F6] flex items-center justify-between">
            <span className="text-xs font-bold text-[#1E40AF]">سجل العمليات</span>
            <div className="flex items-center gap-1">
              {unread > 0 && (
                <button onClick={markAllRead} data-testid="hr-bell-mark-read" className="text-[10px] bg-[#3B82F6] text-white rounded-full px-2 py-0.5 font-bold hover:bg-[#2563eb]">
                  تعليم الكل كمقروء
                </button>
              )}
              <span className="text-[10px] bg-white/70 rounded-full px-2 py-0.5 font-bold" dir="ltr">{unread}/{logs.length}</span>
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-[#F3F6F4]" data-testid="hr-bell-activity-list">
            {logs.length === 0 ? (
              <p className="text-xs text-[#5C6E64] text-center py-6 px-3">لا توجد عمليات بعد. عند حذف أي شيء ستظهر هنا.</p>
            ) : (
              logs.map((row) => (
                <div key={row.id} data-testid={`activity-row-${row.id}`} className={`px-3 py-2 ${row.read ? "" : "bg-[#FFFBEB]"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-bold text-[#1E3A5F] leading-snug">{row.summary}</p>
                      <p className="text-[10px] text-[#5C6E64] mt-0.5">
                        <span className="font-bold">{row.actor_name}</span>
                        <span className="mx-1">·</span>
                        <span dir="ltr">{fmtAgo(row.created_at)}</span>
                        <span className="mx-1">·</span>
                        <span className="inline-block bg-[#E4EBE6] rounded px-1.5 py-px text-[#5C6E64]">{row.resource_type}</span>
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default BellDraftsButton;
